import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useState } from 'react'
import cn from 'classnames/bind'

import useCallbackRef from 'app/common/hooks/useCallbackRef'

import Panel from './panel'
import PanelHeader from './panel-header'
import useFancyStyles, { emptyStyle, emptyStyleBorder } from './use-fancy-styles'
import styles from './plain-accordion.module.scss'

export enum AccordionEvent {
  Collapse = 'collapse',
}

const cx = cn.bind(styles)

type Props = {
  className?: string
  items?: Array<{ ID: string; title: string | ReactNode }>
  locked?: boolean | Set<string>
  panelContent?: (props: {
    ID: string
    active: boolean
    updateSize: () => void
    setActiveID: Dispatch<SetStateAction<string | null>>
    closeActivePanel: () => void
  }) => ReactNode
  headerContent?: (props: {
    ID: string
    active: boolean
    hover: boolean
    setActiveID: Dispatch<SetStateAction<string | null>>
    closeActivePanel: () => void
  }) => ReactNode
  fancyColor?: string
  fancyColorTitle?: string
  defaultActiveTabId?: string
  getHeaderClassName?: (params: { active: boolean }) => string | undefined
  getContentClassName?: (params: { active: boolean }) => string | undefined
  getItemClassName?: (params: { active: boolean }) => string | undefined
  onChangePanelOpenState?: (open: boolean) => void
  onChangeActivePanel?: (panelId: string | null) => void
}

const PlainAccordion = ({
  className,
  items = [],
  locked,
  panelContent = () => <div>{'Нет данных'}</div>,
  headerContent,
  fancyColor,
  fancyColorTitle,
  defaultActiveTabId,
  getHeaderClassName,
  getContentClassName,
  getItemClassName,
  onChangePanelOpenState,
  onChangeActivePanel,
}: Props) => {
  const [activeID, setActiveID] = useState<string | null>(defaultActiveTabId ?? null)

  const closeActivePanel = useCallback(() => {
    setActiveID(null)
  }, [])

  const { fancyStyleHeader, facyStylePanel, fancyStyleTitle } = useFancyStyles(
    fancyColor,
    fancyColorTitle,
  )

  const onChangePanelOpenStateRef = useCallbackRef(onChangePanelOpenState)
  const onChangeActivePanelRef = useCallbackRef(onChangeActivePanel)

  const handleClickHeader = useCallback(
    (ID: string) => {
      if (activeID && ID === activeID) onChangePanelOpenStateRef.current?.(false)
      if (!activeID) onChangePanelOpenStateRef.current?.(true)
      setActiveID((current) => (current === ID ? null : ID))
      onChangeActivePanelRef.current?.(ID)
    },
    [onChangePanelOpenStateRef, activeID],
  )

  useEffect(() => {
    const handleCollapse = () => {
      setActiveID(null)
    }
    window.addEventListener(AccordionEvent.Collapse, handleCollapse)
    return () => {
      window.removeEventListener(AccordionEvent.Collapse, handleCollapse)
    }
  }, [])

  return (
    <ul className={cx(styles.root, className)}>
      {items.map(({ ID, title }) => {
        const active = activeID === ID
        const itemLocked = typeof locked === 'boolean' ? locked : locked?.has(ID)

        return (
          <li
            key={ID}
            className={cx(styles.li, { notActive: !active }, getItemClassName?.({ active }))}
          >
            <PanelHeader
              ID={ID}
              title={title}
              onClick={handleClickHeader}
              locked={itemLocked}
              className={cx(
                styles.header,
                { active, locked: itemLocked },
                getHeaderClassName?.({ active }),
              )}
              titleStyle={active && fancyColor ? fancyStyleTitle : emptyStyle}
              style={active && fancyColor ? fancyStyleHeader : emptyStyleBorder}
            >
              {(hover) =>
                headerContent?.({
                  ID,
                  active,
                  hover,
                  setActiveID,
                  closeActivePanel,
                })
              }
            </PanelHeader>

            <Panel
              active={active}
              className={getContentClassName?.({ active })}
              style={active && fancyColor ? facyStylePanel : emptyStyleBorder}
            >
              {(activeActual, updateSize) =>
                panelContent({
                  ID,
                  active: activeActual,
                  updateSize,
                  setActiveID,
                  closeActivePanel,
                })
              }
            </Panel>
          </li>
        )
      })}
    </ul>
  )
}

export default PlainAccordion
